import { useLazyGetLatestVersionOfDocumentsByPermitRegisterId } from "../operations/queries/getLatestVersionOfDocumentsByPermitRegisterId";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import SelectDocumentModal from "../components/SelectDocumentModal";
import { Document_ListItem } from "../models/Document_ListItem";
import { DocumentType } from "../models/DocumentType";
import { useAppContext } from "../hooks/useAppContext";

interface Props {
  open: boolean;
  onSelectDocument: (doc: Document_ListItem) => void;
  docType?: DocumentType;
  onClose: () => void;
}

const SelectDocumentModalContainer = ({ open, onSelectDocument, docType, onClose }: Props) => {
  const { currentPermitRegister } = useAppContext();
  const [paginationInfo, setPaginationInfo] = useState({ pageSize: 0, pageIndex: 0 });
  const [searchString, setSearchString] = useState("");

  const {
    getLatestVersionOfDocumentsByPermitRegisterId: getDocuments,
    data: documents,
    status: getDocumentsStatus,
  } = useLazyGetLatestVersionOfDocumentsByPermitRegisterId();

  const handleSelectDocument = (doc: Document_ListItem) => {
    onSelectDocument(doc);
  };

  const handlePageInfoChange = useCallback(
    (pageIndex: number, pageSize: number) => {
      if (pageSize !== paginationInfo.pageSize || pageIndex !== paginationInfo.pageIndex) {
        setPaginationInfo({ pageSize, pageIndex });
      }
    },
    [paginationInfo.pageIndex, paginationInfo.pageSize]
  );

  const handleSearchDocuments = debounce((searchString: string) => {
    if (searchString !== undefined) {
      setSearchString(searchString);
    }
    setPaginationInfo({ ...paginationInfo, pageIndex: 0 });
  }, 500);

  useEffect(() => {
    if (currentPermitRegister && paginationInfo.pageSize > 0)
      getDocuments({
        variables: {
          permitRegisterId: currentPermitRegister.id,
          skip: paginationInfo.pageIndex * paginationInfo.pageSize,
          take: paginationInfo.pageSize,
          typeId: docType ? docType.id : undefined,
          searchString,
        },
      });
  }, [paginationInfo.pageSize, paginationInfo.pageIndex, currentPermitRegister, searchString, getDocuments, docType]);

  const handleClose = () => {
    setSearchString("");
    onClose();
  };

  return (
    <SelectDocumentModal
      open={open}
      documents={documents && documents.latestVersionOfDocumentsByPermitRegisterId?.items}
      pageCount={
        documents &&
        Math.ceil((documents.latestVersionOfDocumentsByPermitRegisterId?.totalCount ?? 0) / paginationInfo.pageSize)
      }
      docType={docType}
      onPageInfoChange={handlePageInfoChange}
      onSearchDocuments={handleSearchDocuments}
      getDocumentsStatus={getDocumentsStatus}
      onSelectDocument={handleSelectDocument}
      onClose={handleClose}
    ></SelectDocumentModal>
  );
};

export default SelectDocumentModalContainer;
